import {
  type BoardResourceUrls,
  getBoardResourceUrls,
} from "@/config/boardRuntimeConfig";
import { BOARD_CONFIG } from "@/config/browserBoard";
import {
  getAttachmentSource,
  normalizeAttachmentMimeType,
  readAttachmentForUpload,
  sha256,
} from "@/shared/api/attachmentUpload";
import { ApiError } from "@/shared/api/errors";
import { withRequestDeadline } from "@/shared/api/requestDeadline";
import {
  AimgTokenManager,
  shouldRetryManagedToken,
} from "@/shared/api/tokenManager";
import { API_VERSION, API_VERSION_HEADER } from "@/shared/api/version";
import { getFingerprint } from "@/shared/lib/fingerprint";

export { ApiError } from "@/shared/api/errors";
export { API_VERSION } from "@/shared/api/version";

const BOARD_RESOURCE_URLS: BoardResourceUrls =
  getBoardResourceUrls(BOARD_CONFIG);

export const API_BASE: string = BOARD_RESOURCE_URLS.apiBase;
export const UPLOADS_BASE: string = BOARD_RESOURCE_URLS.uploadsBase;

export interface RequestOptions {
  token?: string;
  requiresToken?: boolean;
  /** 既存tokenがない、または無効な場合のPOST /tokensを許可する。既定はtrue。 */
  allowTokenIssue?: boolean;
  cache?: RequestCache;
  credentials?: RequestCredentials;
  signal?: AbortSignal;
  apiVersion?: string;
  /** 成功したnative Responseのheader等を観測する。 */
  onSuccessResponse?: (response: Response) => void;
  /**
   * X-Board-Keyの上書き。省略時は現在板(BOARD_CONFIG.boardKey)。
   * backendの板解決はpath slug→threadId/postId逆引き→板非依存→headerの順で、
   * headerが効くのは/search・/attachments/presign等のpath無しルートだけ。
   * 総合カタログが他板の板スコープAPIを引くときは、header解決ルートでの
   * 実選択とpath解決ルートでの意図表明のため対象板のslugを必ず明示する。
   */
  boardKey?: string;
}

/** 板API向けheadersを契約どおりに組み立てる。 */
export function createApiHeaders(
  headers?: HeadersInit,
  apiVersion?: string,
  boardKey?: string,
): Headers {
  const result = new Headers(headers);
  result.set(API_VERSION_HEADER, apiVersion ?? API_VERSION);
  result.set("X-Board-Key", boardKey ?? BOARD_CONFIG.boardKey);
  return result;
}

interface TokenIssueResult {
  token: string;
  role: "newbie" | "citizen" | "troll";
}

interface NativeErrorBody {
  error?: unknown;
  code?: unknown;
  message?: unknown;
}

const tokenManager: AimgTokenManager = new AimgTokenManager(issueAimgToken);

export async function apiGet<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>("GET", path, undefined, options);
}

export async function apiPost<T>(
  path: string,
  body: object,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>("POST", path, body, options);
}

export async function apiPut<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>("PUT", path, undefined, options);
}

export async function apiPutJson<T>(
  path: string,
  body: object,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>("PUT", path, body, options);
}

export async function apiDelete<T>(
  path: string,
  body: object,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>("DELETE", path, body, options);
}

export async function getAimgToken(): Promise<string> {
  return tokenManager.get();
}

export function clearAimgToken(): void {
  tokenManager.clear();
}

/**
 * 新規token発行(フィンガープリント送信)を発生させずに、既に発行済みかだけを
 * 確認する。カタログセルのhover先読みのように、ユーザーがスレッドを実際に
 * 開く前の受動的な操作からPOST /tokensを起動したくない箇所で使う。
 */
export function hasAimgToken(): boolean {
  return tokenManager.hasToken();
}

/** React Queryのキーに使うraw tokenを含まない閲覧主体の世代。 */
export function getAimgViewerContext(): number {
  return tokenManager.getViewerContext();
}

export function subscribeAimgViewerContext(listener: () => void): () => void {
  return tokenManager.subscribe(listener);
}

export async function refreshAimgToken(expiredToken: string): Promise<string> {
  return tokenManager.refresh(expiredToken);
}

export async function uploadAttachment(
  file: File,
  signal?: AbortSignal,
  boardKey?: string,
): Promise<string> {
  return withRequestDeadline(
    (boundedSignal) =>
      uploadAttachmentWithinDeadline(file, boundedSignal, boardKey),
    signal,
    60_000,
  );
}

async function uploadAttachmentWithinDeadline(
  file: File,
  signal: AbortSignal,
  boardKey?: string,
): Promise<string> {
  const digest = await sha256(await readAttachmentForUpload(file));
  // ブラウザ由来のfile.typeはimage/jpg等の非標準別名や`; charset=…`パラメータを含むことがある。
  // backendはpresign時にnormalizeMimeで正規化してからS3のpresigned URLを生成するため、
  // frontend側でも同じ正規化を通し、presignで宣言するmimeとPUTのContent-Typeを揃える。
  const canonicalMime = normalizeAttachmentMimeType(file.type);
  // presignはX-Board-Keyだけで板が決まり、attachmentは板に紐づく。
  // 他板へのスレ立てでは対象板のkeyでpresignしないとfinalizeで不一致になる
  const result = await apiPost<PresignResult>(
    "/attachments/presign",
    {
      sha256: digest,
      mime: canonicalMime,
      sizeBytes: file.size,
      fileName: file.name,
      source: getAttachmentSource(file),
    },
    { requiresToken: true, signal, boardKey },
  );

  if (result.uploadUrl) {
    const uploadResponse = await fetch(result.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": canonicalMime },
      body: file,
      signal,
    });
    if (!uploadResponse.ok) {
      throw new ApiError(
        `添付ファイルのアップロードに失敗しました (HTTP ${uploadResponse.status})`,
        uploadResponse.status,
      );
    }
  }

  return result.attachmentId;
}

interface PresignResult {
  attachmentId: string;
  uploadUrl: string | null;
  reused: boolean;
}

async function issueAimgToken(): Promise<string> {
  const fingerprint = await getFingerprint();
  return request<TokenIssueResult>(
    "POST",
    "/tokens",
    {
      metrics: {
        fingerprint,
        userAgent: navigator.userAgent,
        language: navigator.language,
      },
    },
    {
      credentials: "include",
    },
  ).then((result) => result.token);
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: object,
  options: RequestOptions = {},
  retryCount: number = 0,
): Promise<T> {
  return withRequestDeadline(
    (signal) =>
      requestWithinDeadline<T>(
        method,
        path,
        body,
        { ...options, signal },
        retryCount,
      ),
    options.signal,
  );
}

async function requestWithinDeadline<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body: object | undefined,
  options: RequestOptions,
  retryCount: number,
): Promise<T> {
  const headers = createApiHeaders(
    { Accept: "application/json" },
    options.apiVersion,
    options.boardKey,
  );
  let token = options.token;
  if (options.requiresToken && !token) {
    if (options.allowTokenIssue === false && !tokenManager.hasToken()) {
      throw new ApiError(
        "発行済みの閲覧トークンがありません",
        422,
        "TOKEN_NOT_FOUND",
      );
    }
    token = await getAimgToken();
  }
  if (token) headers.set("x-aimg-token", token);

  if (body) headers.set("Content-Type", "application/json");

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: options.credentials ?? "same-origin",
    cache: options.cache,
    signal: options.signal,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const payload: unknown = await readJson(response);
  if (!response.ok) {
    const error = makeApiError(payload, response.status, response.headers);
    if (
      token &&
      options.allowTokenIssue !== false &&
      shouldRetryManagedToken(
        Boolean(options.requiresToken),
        Boolean(options.token),
        retryCount,
        error.code,
      )
    ) {
      const freshToken = await tokenManager.refresh(token);
      return request<T>(
        method,
        path,
        body,
        { ...options, token: freshToken },
        retryCount + 1,
      );
    }
    if (
      (error.code === "TOKEN_EXPIRED" || error.code === "TOKEN_NOT_FOUND") &&
      token
    )
      tokenManager.clear(token);
    throw error;
  }

  options.onSuccessResponse?.(response);
  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function makeApiError(
  payload: unknown,
  status: number,
  headers: Headers,
): ApiError {
  const body = isNativeErrorBody(payload) ? payload : undefined;
  const nested =
    typeof body?.error === "object" && body.error !== null
      ? (body.error as { code?: unknown; message?: unknown })
      : undefined;
  const message =
    typeof nested?.message === "string"
      ? nested.message
      : typeof body?.message === "string"
        ? body.message
        : typeof body?.error === "string"
          ? body.error
          : `HTTP ${status}`;
  const code =
    typeof nested?.code === "string"
      ? nested.code
      : typeof body?.code === "string"
        ? body.code
        : undefined;
  return new ApiError(
    message,
    status,
    code,
    headers.get("Retry-After") ?? undefined,
    payload,
  );
}

function isNativeErrorBody(value: unknown): value is NativeErrorBody {
  return typeof value === "object" && value !== null && "error" in value;
}
